import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MAX_REVIEW_LISTS } from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { lists as listCopy } from "../copy/lists.js";
import type { ListSummary } from "../components/ListGrid.js";
import { Banner, Button, Card, Stars } from "../components/ui.js";

const copy = listCopy.review;
const LANGS = ["en", "fr"] as const;

/**
 * «Wiederholen» (issue #101): tick up to ten lists of one language, own and
 * class lists, archived ones too. The selection lives in the address of the
 * round, so «Nochmals» there takes the same lists; nothing is saved here.
 */
export function ReviewScreen() {
  const navigate = useNavigate();
  const [lists, setLists] = useState<ListSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<string[]>([]);

  useEffect(() => {
    const scopes = ["mine", "class", "archived"] as const;
    void Promise.all(scopes.map((scope) => api<{ lists: ListSummary[] }>(`/api/lists?scope=${scope}`)))
      .then((results) => {
        const byId = new Map<string, ListSummary>();
        for (const list of results.flatMap((r) => r.lists)) if (!byId.has(list.id)) byId.set(list.id, list);
        setLists([...byId.values()].filter((list) => list.item_count > 0));
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : errors.failed));
  }, []);

  const chosen = (lists ?? []).filter((list) => ticked.includes(list.id));
  const lang = chosen[0]?.lang_b ?? null;
  const full = ticked.length >= MAX_REVIEW_LISTS;
  // An upper bound: a word in two lists is asked as one.
  const wordCount = chosen.reduce((sum, list) => sum + list.item_count, 0);

  const toggle = (id: string) =>
    setTicked((now) => (now.includes(id) ? now.filter((t) => t !== id) : [...now, id]));

  return (
    <div className="space-y-6 lg:space-y-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1 font-semibold text-fox-ink underline-offset-4 hover:underline"
      >
        {common.backToStart}
      </Link>
      <div>
        <h1 className="text-3xl sm:text-4xl">{copy.title}</h1>
        <p className="mt-2 max-w-2xl text-ink-soft">{copy.hint}</p>
      </div>
      {error ? <Banner>{error}</Banner> : null}
      {lists === null && !error ? <p className="text-ink-soft">{common.loading}</p> : null}
      {lists && lists.length === 0 ? <p className="text-ink-soft">{copy.empty}</p> : null}

      {LANGS.map((group) => {
        const inGroup = (lists ?? []).filter((list) => list.lang_b === group);
        if (inGroup.length === 0) return null;
        const otherLang = lang !== null && lang !== group;
        return (
          <section key={group} className="space-y-3">
            <h2 className="text-xl sm:text-2xl">{common.lang[group]}</h2>
            {otherLang ? (
              <p id={`review-${group}-hint`} className="text-ink-soft">
                {copy.oneLang}
              </p>
            ) : null}
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {inGroup.map((list) => {
                const on = ticked.includes(list.id);
                const disabled = !on && (otherLang || full);
                return (
                  <Card as="li" key={list.id} className={disabled ? "opacity-50" : ""}>
                    <label
                      className={`flex min-h-16 items-center gap-3 rounded-3xl p-4 ${disabled ? "" : "cursor-pointer"}`}
                    >
                      <input
                        type="checkbox"
                        className="h-6 w-6 shrink-0 accent-fox-deep"
                        checked={on}
                        disabled={disabled}
                        aria-describedby={
                          otherLang ? `review-${group}-hint` : disabled ? "review-full" : undefined
                        }
                        onChange={() => toggle(list.id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-display text-lg leading-snug font-semibold break-words">
                          {list.title}
                        </span>
                        <span className="block text-sm text-ink-soft">
                          {common.words(list.item_count)}
                          {list.mark === "archived" ? ` · ${copy.archived}` : ""}
                        </span>
                      </span>
                      <Stars n={list.stars} label={listCopy.progress.starsAria(list.stars)} />
                    </label>
                  </Card>
                );
              })}
            </ul>
          </section>
        );
      })}

      {/* Above the bottom bar on phone and tablet (Shell), so the button stays
          in reach below a long list. */}
      {lists && lists.length > 0 ? (
        <div className="sticky bottom-20 space-y-2 sm:max-w-sm lg:bottom-6">
          {/* Always in the page, so a screen reader hears it when it fills. */}
          <p id="review-full" aria-live="polite" className="text-ink-soft empty:hidden">
            {full ? copy.maxLists(MAX_REVIEW_LISTS) : ""}
          </p>
          <Button
            disabled={ticked.length === 0}
            onClick={() => navigate(`/practice/review?lists=${ticked.join(",")}`)}
          >
            {ticked.length === 0 ? copy.title : copy.start(wordCount)}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
